// ─── Types ───────────────────────────────────────────────────────────────────
// ─── Helpers ─────────────────────────────────────────────────────────────────
function injectScript(src, onLoad) {
    if (typeof document === "undefined")
        return;
    if (document.querySelector(`script[src="${src}"]`)) {
        onLoad?.();
        return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    if (onLoad)
        s.onload = onLoad;
    document.head.appendChild(s);
}
export function createGA4Adapter({ measurementId }) {
    const queue = [];
    let ready = false;
    // Инициализируем dataLayer и stub-gtag до загрузки скрипта —
    // это стандартный паттерн Google, события не теряются.
    if (typeof window !== "undefined") {
        window.dataLayer = window.dataLayer ?? [];
        if (typeof window.gtag !== "function") {
            window.gtag = function () {
                window.dataLayer.push(arguments);
            };
        }
        window.gtag("js", new Date());
        window.gtag("config", measurementId);
        injectScript(`https://www.googletagmanager.com/gtag/js?id=${measurementId}`, () => {
            ready = true;
            queue.forEach((e) => sendGA4(e));
            queue.length = 0;
        });
    }
    function sendGA4(event) {
        window.gtag("event", event.name, event.params ?? {});
    }
    return {
        name: "GA4",
        track(event) {
            if (typeof window === "undefined")
                return;
            if (ready) {
                sendGA4(event);
            }
            else {
                queue.push(event);
            }
        },
    };
}
export function createYandexMetrikaAdapter({ counterId, initParams, }) {
    const queue = [];
    let ready = false;
    if (typeof window !== "undefined") {
        // Стандартный stub Метрики — события копятся в очереди внутри самой ym
        // до загрузки скрипта. Дополнительно держим свою очередь на случай
        // если ym ещё даже не объявлена.
        window.ym =
            window.ym ??
                function (...args) {
                    (window.ym.a = window.ym.a ?? []).push(args);
                };
        window.ym.l = Date.now();
        const defaultInit = { clickmap: true, webvisor: true, accurateTrackBounce: true };
        window.ym(counterId, "init", { ...defaultInit, ...(initParams ?? {}) });
        injectScript("https://mc.yandex.ru/metrika/tag.js", () => {
            ready = true;
            queue.forEach((e) => sendYM(e));
            queue.length = 0;
        });
    }
    function sendYM(event) {
        window.ym(counterId, "reachGoal", event.name, event.params ?? {});
    }
    return {
        name: "YandexMetrika",
        track(event) {
            if (typeof window === "undefined")
                return;
            if (ready) {
                sendYM(event);
            }
            else {
                queue.push(event);
            }
        },
    };
}
// ─── Console (dev) ────────────────────────────────────────────────────────────
export function createConsoleAdapter() {
    return {
        name: "Console",
        track(event) {
            console.log(`%c[Analytics] ${event.name}`, "color:#6366f1;font-weight:bold", event.params ?? {});
        },
    };
}
// ─── Manager ─────────────────────────────────────────────────────────────────
class AnalyticsManager {
    constructor() {
        this.adapters = [];
        this.initialized = false;
    }
    register(adapter) {
        this.adapters.push(adapter);
        return this;
    }
    track(event) {
        this.adapters.forEach((adapter) => {
            try {
                adapter.track(event);
            }
            catch (err) {
                console.error(`[Analytics/${adapter.name}] Error:`, err);
            }
        });
    }
    isInitialized() {
        return this.initialized;
    }
    markInitialized() {
        this.initialized = true;
    }
}
export const analytics = new AnalyticsManager();
/**
 * Инициализирует аналитику: инжектит скрипты GA4/YM и регистрирует адаптеры.
 * Безопасно вызывать несколько раз — повторные вызовы игнорируются.
 *
 * @example
 * initAnalytics({ ga4MeasurementId: "G-XXXXXX", ymCounterId: 12345678 });
 */
export function initAnalytics(options = {}) {
    if (analytics.isInitialized())
        return;
    analytics.markInitialized();
    if (options.debug) {
        analytics.register(createConsoleAdapter());
    }
    if (options.ga4MeasurementId) {
        analytics.register(createGA4Adapter({ measurementId: options.ga4MeasurementId }));
    }
    if (options.ymCounterId) {
        analytics.register(createYandexMetrikaAdapter({
            counterId: options.ymCounterId,
            initParams: options.ymInitParams,
        }));
    }
}
